import { Component, computed, input, output, signal } from '@angular/core';
import { IntegrationOverview } from '../../models/integration-status';
import { DATE_TIME, DAY, SAST, countPills, pillClasses, tenantNames } from './integration-status.util';

/**
 * "All integrations" in the integration drawer: every FMS source, iGator site and weather station,
 * and the linked fields per client. The same for every Southern Gateway user (decision 26/10/2026);
 * the test tenants are labelled, the retired ones are left out by the server.
 */
@Component({
  selector: 'app-integration-all',
  templateUrl: './integration-all.component.html',
  standalone: false
})
export class IntegrationAllComponent {
  readonly overview = input<IntegrationOverview | null>(null);
  readonly loading = input(false);
  readonly failed = input(false);
  readonly retry = output<void>();

  readonly DATE_TIME = DATE_TIME;
  readonly DAY = DAY;
  readonly SAST = SAST;
  readonly pillClasses = pillClasses;

  /** iGator sites whose systems are listed. */
  readonly expanded = signal<Set<string>>(new Set());

  readonly counts = computed(() => countPills(this.overview()?.counts ?? null));
  private readonly tenants = computed(() => this.overview()?.tenants ?? []);

  readonly fmsRows = computed(() => (this.overview()?.fms ?? []).map(source => ({
    source,
    forClients: this.names(source.tenantIds)
  })));

  readonly siteRows = computed(() => (this.overview()?.igatorSites ?? []).map(site => ({
    site,
    key: site.siteId ?? '',
    forClients: this.names(site.tenantIds)
  })));

  readonly stationRows = computed(() => (this.overview()?.weatherStations ?? []).map(row => ({
    row,
    name: row.station.customName ? `${row.station.customName} · ${row.station.code}` : row.station.code,
    forClients: this.names(row.tenantIds)
  })));

  /** Client tenants with no station: counted as Not set up, so listed with the stations. */
  readonly withoutStation = computed(() => this.tenants()
    .filter(t => !t.test && !t.weatherStation)
    .map(t => t.name || `Tenant ${t.id}`)
    .join(', '));

  readonly tenantRows = computed(() => this.tenants().map(t => ({
    tenant: t,
    name: t.name || `Tenant ${t.id}`,
    station: t.weatherStation ? (t.weatherStation.customName || t.weatherStation.code) : null
  })));

  /** One line per client whose fields use another client's FMS block or iGator system. */
  readonly crossTenantNotes = computed(() => {
    const notes: string[] = [];
    for (const t of this.tenants()) {
      const name = t.name || `Tenant ${t.id}`;
      if (t.crossTenantTaps > 0) {
        const owners = this.names(t.crossTenantSystemTenantIds) || 'another client';
        notes.push(`${t.crossTenantTaps} ${t.crossTenantTaps === 1 ? 'tap' : 'taps'} on ${t.crossTenantTapFields} of ${name}'s fields last ran under a system of ${owners}.`);
      }
      if (t.crossTenantFmsFields > 0) {
        notes.push(`${t.crossTenantFmsFields} of ${name}'s fields ${t.crossTenantFmsFields === 1 ? 'is' : 'are'} linked to another client's FMS block.`);
      }
    }
    return notes;
  });

  toggleSite(key: string): void {
    const next = new Set(this.expanded());
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.expanded.set(next);
  }

  private names(ids: number[]): string {
    return tenantNames(ids, this.tenants()).join(', ');
  }
}
